import React from "react";
import {
  MdAccessTime,
  MdConfirmationNumber,
  MdDashboard,
  MdFolder,
  MdAccountTree,
  MdGroups,
  MdPeopleAlt,
  MdLayers,
  MdReceiptLong,
  MdViewTimeline,
} from "react-icons/md";
import { ClientPortalAreas } from "../../../../interfaces/ClientPortal";
import { abaLiberada, PortalTab } from "../portalAreas";

const ABAS: { id: PortalTab; label: string; icon: React.ReactNode }[] = [
  { id: "overview", label: "Visão geral", icon: <MdDashboard size={16} /> },
  { id: "schedule", label: "Cronograma", icon: <MdViewTimeline size={16} /> },
  { id: "projects", label: "Projetos", icon: <MdFolder size={16} /> },
  { id: "hours", label: "Horas", icon: <MdAccessTime size={16} /> },
  { id: "billings", label: "Faturas", icon: <MdReceiptLong size={16} /> },
  { id: "tickets", label: "Chamados", icon: <MdConfirmationNumber size={16} /> },
  { id: "fila", label: "Fila da equipe", icon: <MdGroups size={16} /> },
  { id: "filas", label: "Filas", icon: <MdLayers size={16} /> },
  { id: "equipe", label: "Projetos e equipe", icon: <MdPeopleAlt size={16} /> },
  { id: "organograma", label: "Organograma", icon: <MdAccountTree size={16} /> },
];

interface Props {
  areas: ClientPortalAreas;
  active: PortalTab;
  onChange: (tab: PortalTab) => void;
  /** `itsm.fila.atender` / `itsm.fila.coordenar`: liberam as abas da fila da organização. */
  podeAtender?: boolean;
  podeCoordenar?: boolean;
  /** `cliente.equipe.gerir`: libera Projetos e equipe, Organograma e Filas (interno ou cliente). */
  podeGerirEquipe?: boolean;
  /** Pessoa de cliente com `portal.projects`: vê Projetos e equipe só para leitura. */
  podeLerEquipe?: boolean;
  /** Pessoa de cliente com `clientportal.view`: vê o Organograma só para leitura. */
  podeLerOrganograma?: boolean;
}

/** Regra de visibilidade das abas que dependem de permissão além da área do portal. */
const abaPermitida = (id: PortalTab, p: Required<Omit<Props, "areas" | "active" | "onChange">>): boolean => {
  if (id === "fila") return p.podeAtender;
  if (id === "filas") return p.podeCoordenar || p.podeGerirEquipe;
  if (id === "equipe") return p.podeGerirEquipe || p.podeLerEquipe;
  if (id === "organograma") return p.podeGerirEquipe || p.podeLerOrganograma;
  return true;
};

/** Abas do portal. Aba de área não liberada nem é renderizada. */
const PortalTabBar: React.FC<Props> = ({
  areas,
  active,
  onChange,
  podeAtender = false,
  podeCoordenar = false,
  podeGerirEquipe = false,
  podeLerEquipe = false,
  podeLerOrganograma = false,
}) => {
  const perm = { podeAtender, podeCoordenar, podeGerirEquipe, podeLerEquipe, podeLerOrganograma };
  return (
  <div className="flex flex-wrap gap-2">
    {ABAS.filter((a) => {
      if (!abaPermitida(a.id, perm)) return false;
      // Quem gere filas enxerga a aba mesmo sem a área Chamados; o servidor é quem manda.
      if (a.id === "filas" && podeGerirEquipe) return true;
      return abaLiberada(a.id, areas);
    }).map((a) => (
      <button
        key={a.id}
        type="button"
        onClick={() => onChange(a.id)}
        className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] ${
          active === a.id
            ? "bg-dv-blue-600 text-white shadow-dv-md"
            : "bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-navy-700"
        }`}
      >
        {a.icon}
        {a.label}
      </button>
    ))}
  </div>
  );
};

export default PortalTabBar;
